import type { FC } from 'react';
import { Grid, useDeviceType, ErrorBoundary } from '@packages/shared';
import { ContentExternal } from '@packages/cms-components';
import type { CmsData } from '@packages/cms-components/interfaces';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import type { SearchSeoApiResponse } from '../../api/rest/types';
import { ProductListSeo } from '../ProductListSeo';
import { ProductCategoryNavigationDesktop } from '../ProductCategoryNavigationDesktop';

export const CategoryPageCmsContentOnlyFragment = graphql(/* GraphQL */ `
  fragment CategoryPageCmsContentOnlyFragment on LandingPageResult {
    LandingPageResultCategoryItems: categories {
      ...ProductCategoryNavigationDesktopFragment
    }
  }
`);

export type CategoryPageCmsContentOnlyProps = {
  /** the cms content to display */
  cmsContent: CmsData;
  /** the category navigation masked data */
  maskedData: FragmentType<typeof CategoryPageCmsContentOnlyFragment>;
  /** the search seo api response */
  seoResponse?: SearchSeoApiResponse;
};

/**
 * Page content for NoProducts CMS page
 * */
export const CategoryPageCmsContentOnly: FC<CategoryPageCmsContentOnlyProps> = ({
  cmsContent,
  maskedData,
  seoResponse,
}) => {
  const data = getFragmentData(CategoryPageCmsContentOnlyFragment, maskedData);

  const { LandingPageResultCategoryItems } = data;

  const { isDesktop } = useDeviceType();

  const showLeftNavigation =
    isDesktop && LandingPageResultCategoryItems && LandingPageResultCategoryItems.length > 0;

  return (
    <Grid container spacing={0} padding={0} columns={5}>
      {showLeftNavigation && (
        <Grid item lg={1}>
          <ProductCategoryNavigationDesktop maskedItems={LandingPageResultCategoryItems} />
        </Grid>
      )}
      <Grid item lg={showLeftNavigation ? 4 : 5} {...(!showLeftNavigation && { width: '100%' })}>
        <ErrorBoundary>
          <ContentExternal data={cmsContent} />
        </ErrorBoundary>

        {seoResponse && <ProductListSeo data={seoResponse} />}
      </Grid>
    </Grid>
  );
};
